"use client";
import { useSetAtom } from "jotai";
import { ReactFlowProvider } from "@xyflow/react";
import {
  Account,
  allowPersistenceInViewOnlyModeAtom,
  autoLayoutSetter,
  BuiltInApp,
  RawCanvas,
  Session,
  saveCurrentNodesAndEdgesSetter,
  Toggles,
  ZSL,
} from "@zapier/canvas-state";
import { useEffect } from "react";

import { Canvas } from "../components/Canvas";
import { CanvasHydrator } from "../components/CanvasHydrator";
import { CanvasBackground } from "../components/CanvasBackground";
import { BuilderNav } from "../components/BuilderNav";
import { EmbedOverlay } from "../components/Canvas/EmbedOverlay";
import useUpdateSelectedApps from "../hooks/useUpdateSelectedApps";
import { CanvasCopilotHydrator } from "../components/CanvasCopilotHydrator";

type Props = {
  canvas?: RawCanvas;
  template?: ZSL;
  chatbotsApp: BuiltInApp;
  tablesApp: BuiltInApp;
  interfacesApp: BuiltInApp;
  agentsApp: BuiltInApp;
  session?: Session;
  account?: Account;
  toggles: Toggles;
  autoLayoutOnLoad?: boolean;
  isEmbedOverride?: boolean;
  isTemplatePage?: boolean;
  isAgentsEnabled: boolean;
};

function getCanvasFromTemplate(template?: ZSL) {
  const templateCanvas = template?.canvases?.[0];

  if (!templateCanvas) return undefined;

  const { name, nodes, edges, description } = templateCanvas;

  return { name, nodes, edges, description };
}

export function IframeEmbeddedCanvas({
  canvas,
  template,
  chatbotsApp,
  tablesApp,
  interfacesApp,
  agentsApp,
  session,
  account,
  toggles,
  autoLayoutOnLoad,
  isEmbedOverride = false,
  isTemplatePage = false,
  isAgentsEnabled,
}: Props) {
  const isInIframe =
    typeof window !== "undefined" && window.self !== window.top;

  const isEmbed = isInIframe || isEmbedOverride;

  const canvasWithFallback = (canvas ||
    getCanvasFromTemplate(template)) as RawCanvas;
  const canvasId = canvasWithFallback?.id;

  useUpdateSelectedApps();

  return (
    <ReactFlowProvider>
      <CanvasHydrator
        canvas={canvasWithFallback}
        template={template}
        chatbotsApp={chatbotsApp}
        tablesApp={tablesApp}
        interfacesApp={interfacesApp}
        agentsApp={agentsApp}
        session={session}
        account={account}
        toggles={toggles}
        isEmbed={isEmbed}
        isTemplatePage={isTemplatePage}
        isAgentsEnabled={isAgentsEnabled}
      >
        <CanvasCopilotHydrator>
          <AutoLayoutPersistSideEffect
            autoLayoutOnLoad={autoLayoutOnLoad}
            canvasId={canvasId}
          />
          {isEmbed ? (
            <>
              <Page />
              <EmbedOverlay />
            </>
          ) : (
            <BuilderNav>
              <Page />
            </BuilderNav>
          )}
        </CanvasCopilotHydrator>
      </CanvasHydrator>
    </ReactFlowProvider>
  );
}

function Page() {
  return (
    <Canvas>
      <CanvasBackground />
    </Canvas>
  );
}

function AutoLayoutPersistSideEffect({
  autoLayoutOnLoad,
  canvasId,
}: {
  autoLayoutOnLoad?: boolean;
  canvasId?: string;
}) {
  const setAutoLayout = useSetAtom(autoLayoutSetter);
  const saveCurrentNodesAndEdges = useSetAtom(saveCurrentNodesAndEdgesSetter);
  const setAllowPersistenceInViewOnlyMode = useSetAtom(
    allowPersistenceInViewOnlyModeAtom,
  );

  useEffect(() => {
    if (!autoLayoutOnLoad) return;
    if (!canvasId) return;

    let isCancelled = false;

    // Allow a one-time save even in view-only mode (embed).
    setAllowPersistenceInViewOnlyMode(true);

    const run = async () => {
      // Give React Flow time to render and measure nodes before layout.
      const LAYOUT_DELAY_MS = 2800;
      await new Promise((r) => setTimeout(r, LAYOUT_DELAY_MS));
      if (isCancelled) return;

      setAutoLayout();

      // Allow the layout setter to update atoms before saving.
      await new Promise((r) => setTimeout(r, 100));
      if (isCancelled) return;

      const res = await saveCurrentNodesAndEdges();
      const success = Boolean(res?.success);

      if (typeof window !== "undefined") {
        const url = new URL(window.location.href);
        // One-shot behavior: remove the flag so refreshes don't re-run autolayout
        // and unexpectedly reposition a canvas the user has manually adjusted.
        url.searchParams.delete("autolayout");
        window.history.replaceState({}, "", url.toString());

        window.parent?.postMessage(
          { type: "canvasAutolayoutComplete", canvasId, success },
          "*",
        );
      }
    };

    run().finally(() => {
      // Turn the escape hatch back off (future saves remain blocked in view-only).
      setAllowPersistenceInViewOnlyMode(false);
    });

    return () => {
      isCancelled = true;
      setAllowPersistenceInViewOnlyMode(false);
    };
  }, [
    autoLayoutOnLoad,
    canvasId,
    saveCurrentNodesAndEdges,
    setAllowPersistenceInViewOnlyMode,
    setAutoLayout,
  ]);

  return null;
}
